/**
 * First-login nickname picker. Shown once when child.display_name is null.
 * Saves via PUT /api/v1/child/me, then triggers a context refresh so the
 * shell + pages can switch over to the chosen nickname.
 */
import { useState } from 'react';
import { api, ApiCallError } from '../api/client';
import { t, useLang } from '../i18n';

interface Props {
  loginName: string;
  onDone: () => void | Promise<void>;
}

export default function NicknameSetupModal({ loginName, onDone }: Props) {
  useLang();
  const [nickname, setNickname] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(value: string) {
    setErr(null);
    const trimmed = value.trim();
    if (trimmed.length < 1 || trimmed.length > 20) {
      setErr(t('nicknameModal.error.length'));
      return;
    }
    setSaving(true);
    try {
      await api.updateMe({ display_name: trimmed });
      await onDone();
    } catch (e) {
      setErr(e instanceof ApiCallError ? e.message : t('nicknameModal.error.saveFailed'));
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16,
      }}
    >
      <div
        className="card"
        style={{ maxWidth: 420, width: '100%', textAlign: 'center', padding: 24 }}
      >
        <div style={{ fontSize: 48, marginBottom: 8 }}>🌟</div>
        <div style={{ fontWeight: 900, fontSize: 22, marginBottom: 6 }}>
          {t('nicknameModal.welcome', { name: loginName })}
        </div>
        <div className="muted" style={{ marginBottom: 16, lineHeight: 1.6 }}>
          {t('nicknameModal.prompt.line1')}<br />
          {t('nicknameModal.prompt.line2')}
        </div>
        <input
          autoFocus
          value={nickname}
          onChange={e => setNickname(e.target.value)}
          maxLength={20}
          placeholder={t('nicknameModal.placeholder')}
          onKeyDown={e => { if (e.key === 'Enter') void save(nickname); }}
          style={{ fontSize: 18, textAlign: 'center' }}
        />
        {err && (
          <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
            {err}
          </div>
        )}
        <div className="flex" style={{ marginTop: 16, gap: 8 }}>
          <button
            className="btn btn-secondary"
            style={{ flex: 1 }}
            disabled={saving}
            onClick={() => void save(loginName)}
            title={t('nicknameModal.keepLoginName.title')}
          >
            {t('nicknameModal.keepLoginName.button', { name: loginName })}
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            disabled={saving || !nickname.trim()}
            onClick={() => void save(nickname)}
          >
            {saving ? <span className="spinner" /> : t('nicknameModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
